import { useSelector } from "react-redux";
import { actionCentreRegistry } from "./registry";
import { useHomeOverallCount } from "../HomeOverallCountContext";
import { checkViewerPermission } from "services/utils/HelperFunction";
import {
  isRequestTypeAccessible,
  WORK_ADJUSTMENT_TABS,
  workAdjustmentTabOf,
} from "./requestTypeAccess";

// Builds the Action Centre groups from the shared consolidated overall-count
// fetch (HomeOverallCountProvider) instead of fanning out to every registry
// entry's own endpoint. Each registry entry keeps its metadata (label, icon
// path, viewAllPath, etc.); we just attach the pending `items` the backend
// already computed for its type and surface only groups that actually have
// items. One entry having no items doesn't hide the others.
export const useActionCentreItems = () => {
  const { itemsByType, loading } = useHomeOverallCount();
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);

  // The backend has no concept of this user's own per-submodule viewer
  // permission, so a type the role can't see in My Requests (e.g. Leave with
  // Leaves' viewer permission off) still arrived here with its pending items
  // -- same gap fixed for the My Requests Summary card and Pending Requests
  // KPI total (useMyPendingRequests.js), reusing the same check.
  const moduleDetails = userScreenAccess?.module_details;

  // Work Adjustment is split into its own Permission / On Duty / Comp Off /
  // Break groups (matching the My Requests menu) instead of one combined
  // "Work Adjustment requests" group. Each group links to its own tab and is
  // gated on its own submodule.
  const groups = actionCentreRegistry
    .flatMap((entry) => {
      const items = itemsByType?.[entry.type] || [];
      if (entry.type === "workAdjustment") {
        return WORK_ADJUSTMENT_TABS.filter((t) =>
          checkViewerPermission(moduleDetails, "My Request", t.submodule),
        ).map((t) => ({
          ...entry,
          type: `workAdjustment-${t.tab}`,
          itemLabel: `${t.label} request`,
          viewAllPath: `/admin/myzone/apply?view=work-adjustment&tab=${t.tab}`,
          items: items.filter((item) => workAdjustmentTabOf(item) === t.tab),
        }));
      }
      if (!isRequestTypeAccessible(moduleDetails, entry.type)) return [];
      return [{ ...entry, items }];
    })
    .filter((group) => group.items.length > 0);

  const count = groups.reduce((sum, group) => sum + group.items.length, 0);

  return { groups, count, loading };
};
